<script lang="ts" setup>
import { useDisplay } from 'vuetify';
import { faRightToBracket, faStarShooting } from '@fortawesome/pro-solid-svg-icons';
import Card from './Card.vue';
import { useConfigHelper } from '@ilteducation/config';
import { useLocale } from '../../composables/useLocale';

const { createQRCodeSupportLink } = useConfigHelper();
const { xs } = useDisplay();

defineProps({
  showSupportCard: {
    type: Boolean,
    default: false,
  },
  onlyEmployeeCreatedMode: {
    type: Boolean,
    default: false,
  },
  isDownloadingLoginCards: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: undefined,
  },
  showGoToUserProfile: {
    type: Boolean,
    default: false,
  },
  showDone: {
    type: Boolean,
    default: true,
  },
});

const emits = defineEmits<{
  (e: 'createMoreUsersClick'): void;
  (e: 'downloadLoginCardsClick'): void;
  (e: 'goToProfileClick'): void;
  (e: 'doneClick'): void;
}>();

const { t } = useLocale();

const gotoSupportClick = () => {
  window.open(createQRCodeSupportLink(), '_blank');
};
</script>
<template>
  <v-container fluid class="pt-0">
    <v-row class="d-flex justify-center">
      <v-col cols="12" lg="auto" class="d-flex justify-center">
        <Card
          :header-icon="faRightToBracket"
          button-prepend-icon="mdi mdi-download"
          :button-text="t('lcp.confirmation_login_card_button')"
          :subtitle="t('lcp.confirmation_login_card_subtitle')"
          @button-clicked="() => emits('downloadLoginCardsClick')"
          :loading="isDownloadingLoginCards"
          :buttonDisabled="onlyEmployeeCreatedMode"
        >
          {{
            onlyEmployeeCreatedMode
              ? t('lcp.confirmation_login_card_info_only_employees')
              : t('lcp.confirmation_login_card_info')
          }}
        </Card>
      </v-col>
      <v-col v-if="showSupportCard" cols="12" lg="auto" class="d-flex justify-start">
        <Card
          :header-icon="faStarShooting"
          :subtitle="t('lcp.confirmation_support_card_subtitle')"
          button-prepend-icon="mdi mdi-open-in-new"
          :button-text="t('lcp.confirmation_support_card_button')"
          @button-clicked="gotoSupportClick"
          button-class="bg-secondary-container"
        >
          {{ t('lcp.confirmation_support_card_info') }}
        </Card>
      </v-col>
    </v-row>
    <v-row class="d-flex justify-center">
      <v-col class="pt-2 d-flex justify-center">
        <v-span v-if="message" class="text-center">{{ t(message) }}</v-span>
      </v-col>
    </v-row>
    <v-row class="d-flex justify-center">
      <v-col cols="auto">
        <v-btn
          variant="outlined"
          prepend-icon="mdi mdi-plus"
          :block="xs"
          rounded="lg"
          @click="() => emits('createMoreUsersClick')"
          min-width="250"
        >
          {{ t('lcp.confirmation_create_users_card_button') }}
        </v-btn>
      </v-col>
      <v-col v-if="showGoToUserProfile" cols="auto">
        <v-btn
          color="primary"
          variant="flat"
          append-icon="mdi mdi-chevron-right"
          :block="xs"
          rounded="lg"
          @click="() => emits('goToProfileClick')"
          min-width="250"
        >
          {{ t('lcp.go_to_user_profile') }}
        </v-btn>
      </v-col>
      <v-col v-if="showDone" cols="auto">
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi mdi-check"
          :block="xs"
          rounded="lg"
          @click="() => emits('doneClick')"
          min-width="250"
        >
          {{ t('lcp.done') }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>
